import type { ImageSources } from '@/components/Image/ArtDirectedImage';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import type { VimeoVideoType } from '@/components/VimeoVideo/VimeoVideo';
import { ContentStyle } from '@/contentful/contentful.types';
import type {
  Contentful,
  FeaturedComponent,
  FrontifyImageContent,
  IconNameContent,
} from '@/contentful/contentful-exports';
import useViewPortSize from '@/hooks/useViewportSize';
import { ViewportSize } from '@/styles/viewportSize';
import { getBoundedAspectRatio } from '@/utils/media';

import type { StrandedIntakeContent } from './EligibilityCheckForm/EligibilityCheckForm';
import HighlightMediaTextFiftyFifty from './HighlightMediaTextFiftyFifty/HighlightMediaTextFiftyFifty';
import HighlightMediaTextFull, {
  type HighlightMediaTextProps,
} from './HighlightMediaTextFull/HighlightMediaTextFull';
import type { TextContentAppLinks } from './TextContent/TextContent';

export enum MobileOrientation {
  IMAGE_FIRST = 'Image first',
  TEXT_FIRST = 'Text first',
}

export interface HighlightIconText extends Contentful {
  fields: {
    name: string;
    icon?: IconNameContent;
    text?: string;
  };
}

export interface HighlightImageTextContent extends Contentful {
  fields: {
    variant: string;
    style?: ContentStyle;
    textAlignment?: string;
    eyebrow?: string;
    headline?: string;
    subhead?: string;
    images?: FrontifyImageContent[];
    inferImageAspectRatioOnMobile?: boolean;
    features?: HighlightIconText[];
    cta?: ButtonContent[];
    isHeroMode?: boolean;
    video?: VimeoVideoType;
    imageAltText?: string;
    appLinks?: TextContentAppLinks;
    mobileOrientation?: MobileOrientation;
    intake?: StrandedIntakeContent;
  };
}

type HighlightImageTextProps = FeaturedComponent<{
  content: HighlightImageTextContent;
}>;

const getImageAspectRatio = (images: FrontifyImageContent[] | undefined) => {
  if (!images || images.length < 1) return `1 / 1`;
  const [primaryImage, mobileOnlyImage] = images;

  if (mobileOnlyImage) {
    return getBoundedAspectRatio(mobileOnlyImage.width, mobileOnlyImage.height);
  }

  return getBoundedAspectRatio(primaryImage.width, primaryImage.height);
};

export default function HighlightMediaText(props: HighlightImageTextProps) {
  const {
    isFeatured,
    content: {
      fields: {
        variant,
        style,
        textAlignment,
        eyebrow,
        headline,
        images,
        inferImageAspectRatioOnMobile,
        subhead,
        features,
        cta,
        isHeroMode,
        video,
        imageAltText,
        appLinks,
        mobileOrientation,
        intake,
      },
    },
  } = props;
  const isOnDark = style === ContentStyle.OnDark;
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isMobile = isExtraSmall || isSmall;
  const isFull = variant === 'Full';
  const isRightAligned = textAlignment === 'Right';

  const mobileImageAspectRatio = inferImageAspectRatioOnMobile
    ? getImageAspectRatio(images)
    : undefined;

  const castedImages = images as unknown as FrontifyImageContent[];
  const hasImages = Array.isArray(castedImages) && castedImages.length > 0;

  const imageSources: ImageSources | undefined = hasImages
    ? {
        xs: [castedImages?.[1], ViewportSize.sm],
        sm: [castedImages?.[1], ViewportSize.md],
        md: [castedImages?.[0], ViewportSize.lg],
        lg: [castedImages?.[0], ViewportSize.xl],
        xl: [castedImages?.[0], ViewportSize.xl],
        fallback: [castedImages?.[0], ViewportSize.md],
      }
    : undefined;

  const highlightMediaTextProps: HighlightMediaTextProps = {
    isMobile,
    imageSources,
    imageAltText,
    mobileImageAspectRatio,
    video,
    isOnDark,
    isRightAligned,
    subhead,
    cta,
    features,
    isHeroMode,
    eyebrow,
    headline,
  };

  return isFull ? (
    <HighlightMediaTextFull {...highlightMediaTextProps} />
  ) : (
    <HighlightMediaTextFiftyFifty
      {...highlightMediaTextProps}
      isFeatured={isFeatured}
      appLinks={!isHeroMode ? appLinks : undefined}
      mobileOrientation={mobileOrientation}
      intake={intake}
    />
  );
}
